import { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Space,
  Statistic,
  Switch,
  Typography,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { PROMO_IMAGE_TYPES, promoImageProblem } from '../promotions/promotions';

/** Estimated talk time for one mode, as the API computes it from live pricing. */
export interface NewUserPromoMinutes {
  minCoinsPerMin: number;
  maxCoinsPerMin: number;
  minMinutes: number;
  maxMinutes: number;
}

export interface NewUserPromoConfig {
  enabled: boolean;
  /** COINS, not rupees — the user's spending wallet is a coin ledger. */
  coins: number;
  /** Only accounts younger than this qualify. 0 = no limit. */
  maxAccountAgeDays: number;
  /** Object key from the shared campaign-image upload. Never the bytes. */
  imageKey: string | null;
  title: string;
  message: string;
  ctaText: string;
  showAudioMinutes: boolean;
  showVideoMinutes: boolean;
  /** The NEW-USER automatic call. Separate from the Call Settings switch,
   *  which still governs the existing zero-balance trigger. */
  autoCallEnabled: boolean;
  autoCallDelaySec: number;
}

export interface NewUserPromoView extends NewUserPromoConfig {
  imageUrl: string | null;
  audioMinutes: NewUserPromoMinutes | null;
  videoMinutes: NewUserPromoMinutes | null;
  grantsTotal: number;
  coinsTotal: number;
}

/** Exactly the server's bounds. The API validates AND clamps on read. */
export const NEW_USER_PROMO_LIMITS = {
  coins: { min: 0, max: 100_000 },
  maxAccountAgeDays: { min: 0, max: 365 },
  autoCallDelaySec: { min: 0, max: 600 },
  titleMax: 80,
  messageMax: 200,
  ctaTextMax: 40,
} as const;

export const NEW_USER_PROMO_KEY = ['admin', 'new-user-promo', 'config'];

/**
 * Exactly the fields the API accepts — it rejects anything extra (strict Zod).
 * `imageKey: null` clears the image; the field is always sent so Remove works.
 */
export function newUserPromoBody(v: NewUserPromoConfig): NewUserPromoConfig {
  return {
    enabled: v.enabled === true,
    coins: v.coins,
    maxAccountAgeDays: v.maxAccountAgeDays ?? 0,
    imageKey: v.imageKey && v.imageKey.length > 0 ? v.imageKey : null,
    title: v.title.trim(),
    message: v.message.trim(),
    ctaText: v.ctaText.trim(),
    showAudioMinutes: v.showAudioMinutes === true,
    showVideoMinutes: v.showVideoMinutes === true,
    autoCallEnabled: v.autoCallEnabled === true,
    autoCallDelaySec: v.autoCallDelaySec ?? 0,
  };
}

/** "≈ 1–3 min", or "≈ 3 min" when every tier costs the same. Null renders "—". */
export function minutesLabel(m: NewUserPromoMinutes | null): string {
  if (!m) return '—';
  return m.minMinutes === m.maxMinutes
    ? `≈ ${m.minMinutes} min`
    : `≈ ${m.minMinutes}–${m.maxMinutes} min`;
}

/** The rates the estimate came from, so an operator can sanity-check it. */
export function ratesLabel(m: NewUserPromoMinutes | null): string {
  if (!m) return 'no tier is priced for this mode';
  return m.minCoinsPerMin === m.maxCoinsPerMin
    ? `${m.minCoinsPerMin} coins/min`
    : `${m.minCoinsPerMin}–${m.maxCoinsPerMin} coins/min across tiers`;
}

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

/**
 * New User Free Credits Promotion.
 *
 * One-off onboarding grant: a newly registered user is credited the configured
 * FREE COINS automatically (no claim button), sees a promotional popup once,
 * and — if the delayed new-user automatic call is on — is offered a call after
 * the configured delay.
 *
 * The amount is in COINS. The user's spending wallet is a coin ledger, so a
 * rupee value here would be meaningless; the ₹ equivalent follows the admin
 * coin rate and is not configured twice.
 */
export function NewUserFreeCreditsCard() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<NewUserPromoConfig>();
  /** Local preview of a just-uploaded file (the server's signed URL arrives on
   *  the next refetch). */
  const [preview, setPreview] = useState<string | null>(null);

  const cfg = useQuery({
    queryKey: NEW_USER_PROMO_KEY,
    queryFn: async () => (await api.get<NewUserPromoView>('/admin/new-user-promo/config')).data,
  });

  useEffect(() => {
    if (cfg.data) {
      form.setFieldsValue(cfg.data);
      setPreview(null);
    }
  }, [cfg.data, form]);

  const save = useMutation({
    mutationFn: async (v: NewUserPromoConfig) =>
      (await api.post<NewUserPromoView>('/admin/new-user-promo/config', newUserPromoBody(v))).data,
    onSuccess: (data) => {
      message.success('Saved — applies to users entering the app from now on.');
      qc.setQueryData(NEW_USER_PROMO_KEY, data);
      void qc.invalidateQueries({ queryKey: NEW_USER_PROMO_KEY });
    },
    onError: (e) => message.error(errMsg(e, 'Save failed')),
  });

  /**
   * Browser → object storage on a short-lived URL, through the EXISTING
   * campaign-image endpoint. Only the returned object key is stored in the
   * configuration; no image bytes go into the database.
   */
  const uploadImage = useMutation({
    mutationFn: async (file: File) => {
      const bad = promoImageProblem(file);
      if (bad) throw new Error(bad);
      const { data: minted } = await api.post<{ key: string; url: string }>(
        '/admin/promos/image/upload-url',
        { contentType: file.type, sizeBytes: file.size },
      );
      const res = await fetch(minted.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      return { key: minted.key, preview: URL.createObjectURL(file) };
    },
    onSuccess: ({ key, preview: local }) => {
      form.setFieldValue('imageKey', key);
      setPreview(local);
      message.info('Image uploaded — press Save to use it.');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : errMsg(e, 'Upload failed')),
  });

  const imageKey = Form.useWatch('imageKey', form);
  const shownImage = preview ?? (imageKey === cfg.data?.imageKey ? cfg.data?.imageUrl : null) ?? null;

  return (
    <Card
      title="New User Free Credits Promotion"
      loading={cfg.isLoading}
      style={{ maxWidth: 700 }}
    >
      <Form<NewUserPromoConfig> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item
          name="enabled"
          label="Promotion enabled"
          valuePropName="checked"
          tooltip="Master switch for the free coins and the welcome popup. Switching it OFF stops new grants; coins already granted are never taken back."
        >
          <Switch />
        </Form.Item>

        <Space size="large" wrap>
          <Form.Item
            name="coins"
            label="Free Coins"
            tooltip="Credited to the user's coin wallet once per account, automatically — there is no claim button. This is COINS, not rupees."
            rules={[
              {
                required: true,
                type: 'number',
                min: NEW_USER_PROMO_LIMITS.coins.min,
                max: NEW_USER_PROMO_LIMITS.coins.max,
              },
            ]}
          >
            <InputNumber
              min={NEW_USER_PROMO_LIMITS.coins.min}
              max={NEW_USER_PROMO_LIMITS.coins.max}
              precision={0}
              addonAfter="coins"
            />
          </Form.Item>
          <Form.Item
            name="maxAccountAgeDays"
            label="Only accounts registered in the last"
            tooltip="Guards against back-crediting your existing user base the moment you switch this on. 0 = no age limit — every account that has never been granted qualifies."
            rules={[
              {
                required: true,
                type: 'number',
                min: NEW_USER_PROMO_LIMITS.maxAccountAgeDays.min,
                max: NEW_USER_PROMO_LIMITS.maxAccountAgeDays.max,
              },
            ]}
          >
            <InputNumber
              min={NEW_USER_PROMO_LIMITS.maxAccountAgeDays.min}
              max={NEW_USER_PROMO_LIMITS.maxAccountAgeDays.max}
              precision={0}
              addonAfter="days"
            />
          </Form.Item>
        </Space>

        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Setting the age limit to 0 credits every existing account that has never received this promotion, the first time each of them opens the app. That is real money and cannot be reversed automatically."
        />

        <Divider orientation="left" plain>
          Popup
        </Divider>

        <Form.Item name="imageKey" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          label="Promotion image"
          tooltip="PNG, JPG or WebP, under 1 MB. Uses the same upload pipeline as the Promotions campaigns — only the storage key is saved."
        >
          <Space>
            {shownImage && (
              <img
                src={shownImage}
                alt=""
                style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover' }}
              />
            )}
            <Upload
              accept={PROMO_IMAGE_TYPES.join(',')}
              showUploadList={false}
              beforeUpload={(file) => {
                uploadImage.mutate(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} loading={uploadImage.isPending}>
                {imageKey ? 'Replace' : 'Upload'}
              </Button>
            </Upload>
            {imageKey && (
              <Button
                type="link"
                danger
                onClick={() => {
                  form.setFieldValue('imageKey', null);
                  setPreview(null);
                }}
              >
                Remove
              </Button>
            )}
          </Space>
        </Form.Item>

        <Form.Item
          name="title"
          label="Popup title"
          rules={[{ required: true, max: NEW_USER_PROMO_LIMITS.titleMax }]}
        >
          <Input maxLength={NEW_USER_PROMO_LIMITS.titleMax} placeholder="Welcome! Free Credits Added" />
        </Form.Item>
        <Form.Item
          name="message"
          label="Popup description"
          rules={[{ required: true, max: NEW_USER_PROMO_LIMITS.messageMax }]}
        >
          <Input.TextArea
            rows={2}
            maxLength={NEW_USER_PROMO_LIMITS.messageMax}
            placeholder="Start connecting with your free credits"
          />
        </Form.Item>
        <Form.Item
          name="ctaText"
          label="Button text"
          rules={[{ required: true, max: NEW_USER_PROMO_LIMITS.ctaTextMax }]}
        >
          <Input maxLength={NEW_USER_PROMO_LIMITS.ctaTextMax} placeholder="Start exploring" />
        </Form.Item>

        <Space size="large" wrap>
          <Form.Item name="showAudioMinutes" label="Show audio minutes" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showVideoMinutes" label="Show video minutes" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>

        {cfg.data && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`At ${cfg.data.coins} coins: audio ${minutesLabel(
              cfg.data.audioMinutes,
            )} (${ratesLabel(cfg.data.audioMinutes)}), video ${minutesLabel(
              cfg.data.videoMinutes,
            )} (${ratesLabel(cfg.data.videoMinutes)}).`}
            description="Estimated from the CURRENT direct-call pricing, which varies by listener tier — the app shows a range for exactly that reason. Change a tier's price on the Call Pricing page and this moves with it. Save to refresh."
          />
        )}

        <Divider orientation="left" plain>
          Automatic call
        </Divider>

        <Form.Item
          name="autoCallEnabled"
          label="Delayed automatic call for new users"
          valuePropName="checked"
          tooltip="Holds the first-entry automatic call back by the delay below. OFF leaves the automatic call exactly as it behaves today. This does NOT affect the existing zero-balance trigger, which always offers immediately."
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="autoCallDelaySec"
          label="Automatic call delay"
          tooltip="Seconds after the user enters the app before the new-user automatic call may be offered."
          rules={[
            {
              required: true,
              type: 'number',
              min: NEW_USER_PROMO_LIMITS.autoCallDelaySec.min,
              max: NEW_USER_PROMO_LIMITS.autoCallDelaySec.max,
            },
          ]}
        >
          <InputNumber
            min={NEW_USER_PROMO_LIMITS.autoCallDelaySec.min}
            max={NEW_USER_PROMO_LIMITS.autoCallDelaySec.max}
            precision={0}
            addonAfter="seconds"
          />
        </Form.Item>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The automatic call itself still needs its master switch on the Settings → Call settings card. This card only adds the new-user delay; the existing zero-balance behaviour is unchanged."
        />

        {cfg.data && (
          <Space size="large" style={{ marginBottom: 16 }}>
            <Statistic title="Accounts credited" value={cfg.data.grantsTotal} />
            <Statistic title="Coins granted" value={cfg.data.coinsTotal} />
          </Space>
        )}

        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Applies within ~60 s (config cache). Each account is credited at most once,
          ever — reinstalling, logging out and back in, or using a second device
          cannot earn a second grant.
        </Typography.Paragraph>

        <Can permission="offers.edit">
          <Button
            type="primary"
            htmlType="submit"
            loading={save.isPending}
            disabled={uploadImage.isPending}
          >
            Save new-user promotion
          </Button>
        </Can>
      </Form>
    </Card>
  );
}
